import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useState } from 'react';

import { UsagePaywall } from '../../components/_molecules/UsagePaywall/UsagePaywall';
import useAccessBadge, {
  LicenseCoarseLevels,
  UserAccessBadge,
} from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import { useMetrics } from '../../hooks/useMetrics';
import backendService from '../../services/backendService';

interface PaywallContextType {
  promptPaywall: (source: string, message?: React.ReactNode) => void;
  hidePaywall: () => void;
  isShowingPaywall: boolean;
  shouldPromptPaywall: () => boolean;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
}

const useHasReachedLimit = () => {
  const { data: hasReachedLimit } = useQuery({
    queryKey: ['hasReachedLimit'],
    queryFn: () => backendService.instance.users.hasReachedCreditsLimit(),
    refetchInterval: 1000 * 60 * 5,
    staleTime: 1000 * 60 * 4,
  });
  return { hasReachedLimit, isReady: hasReachedLimit !== undefined };
};

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const PaywallProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { accessBadge: userAccessBadge, isFetched } = useAccessBadge();
  const { hasReachedLimit, isReady: isReadyHasReachedLimit } = useHasReachedLimit();
  const [isShowingPaywall, setIsShowingPaywall] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState<React.ReactNode>(null);
  const metrics = useMetrics();
  const trackAttioEvent = useAttioEventTracking();

  const shouldPromptPaywall = () => {
    if (!isFetched || !userAccessBadge || !isReadyHasReachedLimit) return false;
    // Show paywall when AI access is blocked
    // Show paywall when credits limit is reached for freemium users
    // Note: Test prep limit is handled separately in TestPrepLastPage component
    return (
      userAccessBadge.license.aiAccessBlocked ||
      (!!hasReachedLimit &&
        userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium)
    );
  };

  const promptPaywall = (source: string, message?: React.ReactNode) => {
    if (shouldPromptPaywall()) {
      // Track the paywall trigger event in Mixpanel
      metrics.logEvent('Paywall.Triggered', {
        source,
        aiAccessBlocked: userAccessBadge?.license?.aiAccessBlocked,
        hasReachedLimit,
        plan: userAccessBadge?.license?.coarseLevel,
        subPlan: userAccessBadge?.license?.fineLevel,
        organizationId: userAccessBadge?.organization?.id,
      });

      trackAttioEvent('paywall_triggered', {
        organizationId: userAccessBadge?.organization?.id,
        userRole: userAccessBadge?.user?.profileInfo?.role,
        userId: userAccessBadge?.user?.id,
        email: userAccessBadge?.user?.email,
        firstName: userAccessBadge?.user?.firstName,
        lastName: userAccessBadge?.user?.lastName,
        source,
      });

      setPaywallMessage(message || null);
      setIsShowingPaywall(true);
    }
  };

  const hidePaywall = () => {
    // Track when paywall is dismissed/closed in Mixpanel
    metrics.logEvent('Paywall.Dismissed');

    setIsShowingPaywall(false);
    setPaywallMessage(null);
  };

  const contextValue: PaywallContextType = {
    promptPaywall,
    hidePaywall,
    isShowingPaywall,
    shouldPromptPaywall,
    isReady: isFetched,
    userAccessBadge,
  };

  return (
    <PaywallContext.Provider value={contextValue}>
      {children}
      <UsagePaywall
        open={isShowingPaywall}
        onClose={hidePaywall}
        message={paywallMessage}
        canPurchaseIndividualPremium={
          userAccessBadge?.canPurchaseIndividualPremium ?? false
        }
      />
    </PaywallContext.Provider>
  );
};

export const usePaywall = (): PaywallContextType => {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};
